import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon, 
  MagnifyingGlassIcon,
  UserIcon,
  ChevronDownIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../contexts/AuthContext';
// import CountrySwitcher from './CountrySwitcher';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile, loading, forceLogout } = useAuthContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/businesses?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    } else {
      navigate('/businesses');
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Debug logging
  // console.log('Header auth state:', { 
  //   currentUser: currentUser?.uid, 
  //   userProfile: userProfile?.profile.firstName, 
  //   loading 
  // });

  // Check if we're on the sell business page
  const isSellBusinessPage = location.pathname === '/sell-business';
  
  // Check if we're on step 3 (has draft parameter, indicating we're past step 1)
  const searchParams = new URLSearchParams(location.search);
  const hasDraft = searchParams.has('draft');
  const isStep3 = isSellBusinessPage && hasDraft;
  
  // Check if we're on an admin page
  const isAdminPage = location.pathname.startsWith('/admin');
  
  // Check if user is admin
  const isAdmin = currentUser && userProfile?.profile.userType === 'admin';

  // Show admin header only when on admin pages AND user is admin
  const showAdminHeader = isAdminPage && isAdmin;

  const navigationItems = [
    { name: 'Search Businesses', href: '/businesses' },
    { name: 'Search Franchises', href: '/franchises' },
    { name: 'Sell My Business', href: '#', hasDropdown: true, dropdownItems: [
      { name: 'Sell Business', href: '/sell-business?type=business' },
      { name: 'Sell Franchise', href: '/sell-business?type=franchise' }
    ]},
    { name: 'Brokers', href: '/brokers' },
    // { name: 'Real Estate', href: '/real-estate' },
    { name: 'Contact us', href: '/resources', hasDropdown: true },
  ];

  const handleDropdownToggle = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Check if clicking outside of any dropdown container
      if (!target.closest('.relative.group')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, []);

  return (
    <>
      {/* Admin Header - No header wrapper */}
      {showAdminHeader && (
        <div className="bg-dark-800 shadow-sm border-b border-gray-700 z-50">
          {/* Top bar with logo and admin actions */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-3 lg:py-4">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-2" onClick={closeMobileMenu}>
                <div className="flex items-center">
                  <img 
                    src="/assets/images/bbm-logo-white.png" 
                    alt="BizBuyMax Logo" 
                    style={{ height: '4.25rem' }}
                    className="w-auto"
                  />
                </div>
                {isSellBusinessPage && (
                  <span className="text-gray-400 italic text-lg ml-3">Create Listing</span>
                )}
              </Link>

              {/* Right side elements - grouped together */}
              <div className="flex items-center space-x-4">
                {/* <CountrySwitcher /> */}
                <button
                  onClick={forceLogout}
                  className="hidden md:block bg-red-500 hover:bg-red-600 text-white px-4 lg:px-6 py-2 rounded-md font-medium transition-colors text-sm lg:text-base"
                >
                  Logout
                </button>
                
                {/* Admin My Account Dropdown */}
                <div className="hidden md:block relative group">
                  {loading ? (
                    // Loading state
                    <div className="flex items-center space-x-2 text-gray-400 text-sm">
                      <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                      </div>
                      <span className="hidden lg:inline text-white">Loading...</span>
                    </div>
                  ) : currentUser ? (
                    // Logged in - My Account dropdown
                    <div className="relative">
                      <button
                        onClick={() => handleDropdownToggle('account')}
                        className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors text-sm"
                      >
                        <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden">
                          {userProfile?.profile.avatar ? (
                            <img 
                              src={userProfile.profile.avatar} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <UserIcon className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <span className="hidden lg:inline font-medium text-white">
                          {userProfile?.profile.firstName || 'My Account'}
                        </span>
                        <ChevronDownIcon className="h-4 w-4 text-white" />
                      </button>

                      {/* My Account Dropdown */}
                      {activeDropdown === 'account' && (
                        <div className="absolute top-full right-0 mt-1 w-56 bg-dark-800 rounded-md shadow-lg border border-gray-700 z-50 group">
                          <div className="py-2">
                            {/* User Info */}
                            <div className="px-4 py-3 border-b border-gray-700">
                              <p className="text-sm font-medium text-white">
                                {userProfile?.profile.firstName} {userProfile?.profile.lastName}
                              </p>
                              <p className="text-xs text-gray-400">{userProfile?.profile.email}</p>
                            </div>

                            {/* Menu Items */}
                            <div className="py-1">
                              <Link
                                to="/admin/dashboard"
                                onClick={closeDropdown}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                              >
                                <Cog6ToothIcon className="h-4 w-4 mr-3" />
                                Admin Dashboard
                              </Link>
                              <Link
                                to="/admin/users"
                                onClick={closeDropdown}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                              >
                                <UserGroupIcon className="h-4 w-4 mr-3" />
                                Users
                              </Link>
                              <Link
                                to="/admin/listings"
                                onClick={closeDropdown}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                              >
                                <DocumentTextIcon className="h-4 w-4 mr-3" />
                                Listings
                              </Link>
                              <Link
                                to="/admin/messages"
                                onClick={closeDropdown}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                              >
                                <DocumentTextIcon className="h-4 w-4 mr-3" />
                                Messages
                              </Link>
                            </div>

                            {/* Logout */}
                            <div className="border-t border-gray-700 pt-1">
                              <button
                                onClick={forceLogout}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-red-400 transition-colors"
                              >
                                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                                Sign Out
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Not logged in - Sign In button
                    <Link
                      to="/signin"
                      className="flex items-center space-x-1 text-gray-300 hover:text-white transition-colors text-sm font-medium"
                    >
                      <UserIcon className="h-4 w-4 lg:h-5 lg:w-5" />
                      <span className="hidden lg:inline">Sign In</span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Regular Header - With header wrapper */}
      {!showAdminHeader && (
        <header className="bg-dark-800 shadow-sm border-b border-gray-700 z-50">
          {/* Top bar with logo and sell button */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-3 lg:py-4">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-2" onClick={closeMobileMenu}>
                <div className="flex items-center">
                  <img 
                    src="/assets/images/bbm-logo-white.png" 
                    alt="BizBuyMax Logo" 
                    style={{ height: '4.25rem' }}
                    className="w-auto"
                  />
                </div>
                {isSellBusinessPage && (
                  <span className="text-gray-400 italic text-lg ml-3">Create Listing</span>
                )}
              </Link>

              {/* Right side elements - grouped together */}
              <div className="flex items-center space-x-4">
                {/* <CountrySwitcher /> */}
                {/* Desktop Brokers Button */}
                {!isSellBusinessPage && (
                  <Link to="/brokers" className="hidden md:block border-2 border-gray-300 text-gray-300 hover:bg-gray-300/10 px-4 lg:px-6 py-2 rounded-md font-medium transition-colors text-sm lg:text-base">
                    Brokers - Advertise with Us
                  </Link>
                )}
                {/* Desktop Sell Button */}
                {!isSellBusinessPage && (
                  <Link to="/sell-business" className="hidden md:block bg-secondary-500 hover:bg-secondary-600 text-white px-4 lg:px-6 py-2 rounded-md font-medium transition-colors text-sm lg:text-base">
                    Sell Your Business
                  </Link>
                )}

                {/* Desktop My Account Dropdown - Non-Admin Users */}
                {(!isSellBusinessPage || isStep3) && (
                  <div className="hidden md:block relative group">
                    {loading ? (
                      // Loading state
                      <div className="flex items-center space-x-2 text-gray-400 text-sm">
                        <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                        </div>
                        <span className="hidden lg:inline text-white">Loading...</span>
                      </div>
                    ) : currentUser ? (
                      // Logged in - My Account dropdown
                      <div className="relative">
                        <button
                          onClick={() => handleDropdownToggle('account')}
                          className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors text-sm"
                        >
                          <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden">
                            {userProfile?.profile.avatar ? (
                              <img 
                                src={userProfile.profile.avatar} 
                                alt="Profile" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <UserIcon className="h-4 w-4 text-white" />
                            )}
                          </div>
                          <span className="hidden lg:inline font-medium text-white">
                            {userProfile?.profile.firstName || 'My Account'}
                          </span>
                          <ChevronDownIcon className="h-4 w-4 text-white" />
                        </button>

                        {/* My Account Dropdown */}
                        {activeDropdown === 'account' && (
                          <div className="absolute top-full right-0 mt-1 w-56 bg-dark-800 rounded-md shadow-lg border border-gray-700 z-50 group">
                            <div className="py-2">
                              {/* User Info */}
                              <div className="px-4 py-3 border-b border-gray-700">
                                <p className="text-sm font-medium text-white">
                                  {userProfile?.profile.firstName} {userProfile?.profile.lastName}
                                </p>
                                <p className="text-xs text-gray-400">{userProfile?.profile.email}</p>
                              </div>

                              {/* Menu Items */}
                              <div className="py-1">
                                <Link
                                  to="/account/dashboard"
                                  onClick={closeDropdown}
                                  className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                                >
                                  <Cog6ToothIcon className="h-4 w-4 mr-3" />
                                  Dashboard
                                </Link>
                                <Link
                                  to="/account/listings"
                                  onClick={closeDropdown}
                                  className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                                >
                                  <DocumentTextIcon className="h-4 w-4 mr-3" />
                                  My Listings
                                </Link>
                                <Link
                                  to="/account/leads"
                                  onClick={closeDropdown}
                                  className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                                >
                                  <UserGroupIcon className="h-4 w-4 mr-3" />
                                  My Leads
                                </Link>
                                <Link
                                  to="/account/saved-listings"
                                  onClick={closeDropdown}
                                  className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                                >
                                  <HeartIcon className="h-4 w-4 mr-3" />
                                  Saved Listings
                                </Link>
                                <Link
                                  to="/account/profile"
                                  onClick={closeDropdown}
                                  className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                                >
                                  <UserIcon className="h-4 w-4 mr-3" />
                                  Profile
                                </Link>
                                <Link
                                  to="/account/upgrades"
                                  onClick={closeDropdown}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                                >
                                  <CurrencyDollarIcon className="h-4 w-4 mr-3" />
                                  Upgrades
                                </Link>
                                {userProfile?.profile.userType === 'admin' && (
                                  <Link
                                    to="/admin/dashboard"
                                    onClick={closeDropdown}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                                  >
                                    <Cog6ToothIcon className="h-4 w-4 mr-3" />
                                    Admin Dashboard
                                  </Link>
                                )}
                              </div>

                              {/* Logout */}
                              <div className="border-t border-gray-700 pt-1">
                                <button
                                  onClick={forceLogout}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-red-400 transition-colors"
                                >
                                  <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                                  Sign Out
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Not logged in - Sign In button
                      <Link
                        to="/signin"
                        className="flex items-center space-x-1 text-gray-300 hover:text-white transition-colors text-sm font-medium"
                      >
                        <UserIcon className="h-4 w-4 lg:h-5 lg:w-5" />
                        <span className="hidden lg:inline">Sign In</span>
                      </Link>
                    )}
                  </div>
                )}

                {/* Mobile Menu Button */}
                {!isSellBusinessPage && (
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="md:hidden p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                  >
                    {mobileMenuOpen ? (
                      <XMarkIcon className="h-6 w-6" />
                    ) : (
                      <Bars3Icon className="h-6 w-6" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Desktop Navigation Bar */}
            {!isSellBusinessPage && (
              <nav className="hidden md:flex items-center justify-between space-x-6 lg:space-x-8 py-3 border-t border-gray-700" ref={dropdownRef}>
              <div className="flex items-center space-x-6 lg:space-x-8">
              {navigationItems.map((item) => (
                <div key={item.name} className="relative group">
                  {item.hasDropdown ? (
                    <button
                      onClick={() => handleDropdownToggle(item.name)}
                      className="flex items-center space-x-1 text-gray-300 hover:text-white font-medium transition-colors text-sm lg:text-base"
                    >
                      <span>{item.name}</span>
                      <ChevronDownIcon className="h-4 w-4" />
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className="flex items-center space-x-1 text-gray-300 hover:text-white font-medium transition-colors text-sm lg:text-base"
                    >
                      <span>{item.name}</span>
                    </Link>
                  )}

                  {/* Desktop Dropdown Menu */}
                  {item.hasDropdown && activeDropdown === item.name && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-dark-800 rounded-md shadow-lg border border-gray-700 z-50">
                      <div className="py-1">
                        {item.dropdownItems ? (
                          // Custom dropdown items (for Selling)
                          item.dropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.href}
                              onClick={closeDropdown}
                              className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                            >
                              {dropdownItem.name}
                            </Link>
                          ))
                        ) : (
                          // Default dropdown items based on navigation name
                          (() => {
                            switch (item.name) {
                              case 'Businesses':
                                return [
                                  { name: 'All Businesses', href: '/businesses' },
                                  { name: 'Restaurants', href: '/businesses?category=restaurant' },
                                  { name: 'Retail', href: '/businesses?category=retail' },
                                  { name: 'Services', href: '/businesses?category=services' },
                                  { name: 'Manufacturing', href: '/businesses?category=manufacturing' }
                                ];
                              case 'Franchises':
                                return [
                                  { name: 'All Franchises', href: '/franchises' },
                                  { name: 'Food & Beverage', href: '/franchises?category=food' },
                                  { name: 'Retail', href: '/franchises?category=retail' },
                                  { name: 'Services', href: '/franchises?category=services' },
                                  { name: 'Health & Fitness', href: '/franchises?category=health' }
                                ];
                              case 'Resources':
                                return [
                                  { name: 'Buying Guide', href: '/resources/buying-guide' },
                                  { name: 'Selling Guide', href: '/resources/selling-guide' },
                                  { name: 'Business Valuation', href: '/resources/valuation' },
                                  { name: 'Financing Options', href: '/resources/financing' },
                                  { name: 'Legal Resources', href: '/resources/legal' }
                                ];
                              default:
                                return [];
                            }
                          })().map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.href}
                              onClick={closeDropdown}
                              className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                            >
                              {dropdownItem.name}
                            </Link>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              </div>
              
              {/* Search Bar in Navigation */}
              <div className="relative w-64">
                <input
                  type="text"
                  placeholder="Search businesses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="w-full pl-3 pr-10 py-1.5 border border-gray-600 bg-gray-700 text-white placeholder-gray-400 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
                <button 
                  onClick={handleSearch}
                  className="absolute right-0 top-0 h-full px-3 bg-secondary-500 hover:bg-secondary-600 text-white rounded-r-md transition-colors"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                </button>
              </div>
            </nav>
            )}
          </div>

          {/* Mobile Menu */}
          {!isSellBusinessPage && mobileMenuOpen && (
            <div className="md:hidden bg-dark-800 border-t border-gray-700">
              <div className="px-4 py-2 space-y-1">
                {/* Mobile Brokers Button */}
                <Link to="/brokers" onClick={closeMobileMenu} className="block w-full border-2 border-gray-300 text-gray-300 hover:bg-gray-300/10 px-4 py-3 rounded-md font-medium transition-colors text-base mb-4 text-center">
                  Brokers - Grow With Us!
                </Link>
                {/* Mobile Sell Button */}
                <Link to="/sell-business" onClick={closeMobileMenu} className="block w-full bg-secondary-500 hover:bg-secondary-600 text-white px-4 py-3 rounded-md font-medium transition-colors text-base mb-4 text-center">
                  Sell Your Business
                </Link>

                {/* Mobile Navigation Items */}
                {navigationItems.map((item) => (
                  <div key={item.name} className="border-b border-gray-700 last:border-b-0">
                    {item.hasDropdown ? (
                      <div>
                        <button
                          onClick={() => handleDropdownToggle(item.name)}
                          className="w-full flex items-center justify-between py-3 text-left text-gray-300 hover:text-white font-medium transition-colors text-base"
                        >
                          <span>{item.name}</span>
                          <ChevronDownIcon className={`h-4 w-4 transition-transform text-white ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {/* Mobile Dropdown Menu */}
                        {activeDropdown === item.name && (
                          <div className="pl-4 pb-2 space-y-1">
                            {item.dropdownItems ? (
                              // Custom dropdown items (for Selling)
                              item.dropdownItems.map((dropdownItem) => (
                                <Link
                                  key={dropdownItem.name}
                                  to={dropdownItem.href}
                                  onClick={closeDropdown}
                                  className="block py-2 text-sm text-gray-400 hover:text-white transition-colors"
                                >
                                  {dropdownItem.name}
                                </Link>
                              ))
                            ) : (
                              // Default dropdown items based on navigation name
                              (() => {
                                switch (item.name) {
                                  case 'Businesses':
                                    return [
                                      { name: 'All Businesses', href: '/businesses' },
                                      { name: 'Restaurants', href: '/businesses?category=restaurant' },
                                      { name: 'Retail', href: '/businesses?category=retail' },
                                      { name: 'Services', href: '/businesses?category=services' },
                                      { name: 'Manufacturing', href: '/businesses?category=manufacturing' }
                                    ];
                                  case 'Franchises':
                                    return [
                                      { name: 'All Franchises', href: '/franchises' },
                                      { name: 'Food & Beverage', href: '/franchises?category=food' },
                                      { name: 'Retail', href: '/franchises?category=retail' },
                                      { name: 'Services', href: '/franchises?category=services' },
                                      { name: 'Health & Fitness', href: '/franchises?category=health' }
                                    ];
                                  case 'Resources':
                                    return [
                                      { name: 'Buying Guide', href: '/resources/buying-guide' },
                                      { name: 'Selling Guide', href: '/resources/selling-guide' },
                                      { name: 'Business Valuation', href: '/resources/valuation' },
                                      { name: 'Financing Options', href: '/resources/financing' },
                                      { name: 'Legal Resources', href: '/resources/legal' }
                                    ];
                                  default:
                                    return [];
                                }
                              })().map((dropdownItem) => (
                                <Link
                                  key={dropdownItem.name}
                                  to={dropdownItem.href}
                                  onClick={closeDropdown}
                                  className="block py-2 text-sm text-gray-400 hover:text-white transition-colors"
                                >
                                  {dropdownItem.name}
                                </Link>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={closeMobileMenu}
                        className="block py-3 text-gray-300 hover:text-white font-medium transition-colors text-base"
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}

                {/* Mobile Search Bar */}
                <div className="border-t border-gray-700 pt-4 mt-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search businesses..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleSearchKeyPress}
                      className="w-full pl-3 pr-10 py-2 border border-gray-600 bg-gray-700 text-white placeholder-gray-400 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    />
                    <button 
                      onClick={handleSearch}
                      className="absolute right-0 top-0 h-full px-3 bg-secondary-500 hover:bg-secondary-600 text-white rounded-r-md transition-colors"
                    >
                      <MagnifyingGlassIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Mobile Account Section */}
                <div className="border-t border-gray-700 pt-4 mt-4">
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-400">Loading...</p>
                    </div>
                  ) : currentUser ? (
                    <div className="space-y-2">
                      <div className="px-2 py-2 bg-gray-700 rounded-lg">
                        <p className="text-sm font-medium text-white">
                          {userProfile?.profile.firstName} {userProfile?.profile.lastName}
                        </p>
                        <p className="text-xs text-gray-400">{userProfile?.profile.email}</p>
                      </div>
                      <Link
                        to="/account/dashboard"
                        onClick={closeMobileMenu}
                        className="block py-2 text-gray-300 hover:text-white font-medium transition-colors text-base"
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/account/listings"
                        onClick={closeMobileMenu}
                        className="block py-2 text-gray-300 hover:text-white font-medium transition-colors text-base"
                      >
                        My Listings
                      </Link>
                      <Link
                        to="/account/leads"
                        onClick={closeMobileMenu}
                        className="block py-2 text-gray-300 hover:text-white font-medium transition-colors text-base"
                      >
                        My Leads
                      </Link>
                      <Link
                        to="/account/saved-listings"
                        onClick={closeMobileMenu}
                        className="block py-2 text-gray-300 hover:text-white font-medium transition-colors text-base"
                      >
                        Saved Listings
                      </Link>
                      <Link
                        to="/account/profile"
                        onClick={closeMobileMenu}
                        className="block py-2 text-gray-300 hover:text-white font-medium transition-colors text-base"
                      >
                        Profile
                      </Link>
                      <Link
                        to="/account/upgrades"
                        onClick={closeMobileMenu}
                        className="block py-2 text-gray-300 hover:text-white font-medium transition-colors text-base"
                      >
                        Upgrades
                      </Link>
                      {userProfile?.profile.userType === 'admin' && (
                        <Link
                          to="/admin/dashboard"
                          onClick={closeMobileMenu}
                          className="block py-2 text-gray-300 hover:text-white font-medium transition-colors text-base"
                        >
                          Admin Dashboard
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          forceLogout();
                          closeMobileMenu();
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-red-400 transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                        Sign Out
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/signin"
                      onClick={closeMobileMenu}
                      className="block py-2 text-gray-300 hover:text-white font-medium transition-colors text-base"
                    >
                      Sign In
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </header>
      )}
    </>
  );
};

export default Header; 